import PropTypes from 'prop-types';
import React from 'react';
import Loading from '../../core/Loading';
import TitledPanel from '../../core/TitledPanel';
import { getGraphFromProject, makeCancelable, sortObjects } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import AddClassDialog from '../associated-project/AddClassDialog';
import ExternalTypeLink from './ExternalTypeLink';
import TypeLink from './TypeLink';

const loadOptions = {};

class TypeDisplay extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      totalRelations: 0,
      resources: null,
      exclusionList: [],
      isErrored: null
    };
    this.cancellable = null;
  }

  static getDerivedStateFromProps(props, state) {
    // We use the objects internal version counter set by GS to know if an update has occurred
    const valuesLength = Array.isArray(props.values) ? props.values.length : (props.values ? 1 : 0);
    if (!state.isLoading && props.values && valuesLength !== state.totalRelations) {
      return {
        resources: null,
        isLoading: true,
        totalRelations: valuesLength
      };
    }
    return null;
  }

  componentDidUpdate() {
    const valuesLength = Array.isArray(this.props.values) ? this.props.values.length : (this.props.values ? 1 : 0);
    if (this.state.resources === null && this.props.values && valuesLength > 0) {
      this.collectResourceData();
    }
  }

  componentDidMount() {
    const valuesLength = Array.isArray(this.props.values) ? this.props.values.length : (this.props.values ? 1 : 0);
    if (this.props.values && valuesLength > 0) {
      this.collectResourceData();
    }
    else {
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({ isLoading: false });
    }
  }

  loadData(iriData, options) {
    this.cancellable = makeCancelable(GS.loadDataFromGraph(iriData, options, false));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  collectResourceData() {
    if (this.cancellable && this.cancellable.isCancelled()) return;
    const classIRIs = this.props.classIRIs;
    // First we load association resources
    const opaLoadData = this.props.ontologyProjectAssociation ?
      this.props.ontologyProjectAssociation.map(opa => {
        return {
          graph: this.props.graph,
          iri: opa
        };
      })
      : [];
    this.loadData(opaLoadData, {})
      .promise
      .then(() => {
        // We get all the association information
        const opaSet = this.props.ontologyProjectAssociation ?
          this.props.ontologyProjectAssociation.map(opa => GS.data(opa, this.props.graph))
          :
          [];
        // Within each opa there will be an associatedClass property. We find the project for each class IRI
        // There is an edge case where the same IRI is in two ontologies but totally unlikely
        const loadInfo = classIRIs.map(iri => {
          const relevantOpa = opaSet.find(opa => opa.associatedClass && opa.associatedClass.includes(iri));
          const graphIRI = relevantOpa ? getGraphFromProject(relevantOpa.associatedProject) : this.props.graph;
          return {
            graph: graphIRI,
            iri
          };
        });
        this.loadData(loadInfo, loadOptions)
          .promise
          .then(() => {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            this.processResources(loadInfo);
            if (this.cancellable && this.cancellable.isCancelled()) return;
            this.setState({ isLoading: false });
          })
          .catch((e) => {
            if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
          });
      })
      .catch((e) => {
        if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
      });
  }

  processResources(loadInfo) {
    let valueList = Array.isArray(this.props.values) ? this.props.values : [this.props.values];
    let resources = valueList.map(id => {
      // Use load info to get the right graph
      const graphData = loadInfo.find(item => item.iri === id);
      const graphIRI = graphData ? graphData.graph : null;
      if (!graphIRI || !GS.isLoaded(id, graphIRI)) {
        return { id, notFound: true };
      }
      return GS.data(id, graphIRI);
    });
    sortObjects(resources, 'label');
    const exclusionList = resources ? this.props.classIRIs : [];
    if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ resources, exclusionList });
  }

  monitorLinkUpdate = (iri, property) => {
    if (this.cancellable && this.cancellable.isCancelled()) return;
    if (property === "label") this.setState({
      resources: null,
      isLoading: true
    });
  };

  render() {

    const { id, isLoading, isReadOnly, graph, defaultLang, source, projects, property, title, highlightNotFound, offerInheritance,
      onAddClass, ontologyProjectAssociation, viewLang, textRef, wrap, additionalEBP } = this.props;
    const { resources, exclusionList } = this.state;

    if (isLoading) return <Loading />;

    const output = resources && resources.map((resource) => {
      if (resource.notFound) {
        return <ExternalTypeLink
          key={"rel-" + source + "-" + resource.id}
          property={property}
          highlight={highlightNotFound}
          id={resource.id}
          graph={graph}
          source={id}
          isReadOnly={isReadOnly || resource.id === "Concept" || resource.id === "Label"}
        />;
      }
      else return <TypeLink
        key={"rel-" + source + "-" + resource.id}
        id={resource.id}
        graph={resource.inNamedGraph}
        sourceGraph={graph}
        source={id}
        property={property}
        isReadOnly={isReadOnly}
        updateEvent={this.monitorLinkUpdate}
        viewLang={viewLang}
        defaultLang={defaultLang} />;
    });

    return (
      wrap ?
        <TitledPanel dataTest="classes" height="full" title={title || "Classes"} actions=
          {!isReadOnly ?
            <AddClassDialog
              offerInheritance={offerInheritance}
              projects={projects}
              textRef={textRef}
              graph={graph}
              schemeIRI={id}
              additionalEBP={additionalEBP}
              ontologyProjectAssociation={ontologyProjectAssociation}
              exclude={exclusionList}
              onAddClass={onAddClass}
              viewLang={viewLang}
              defaultLang={defaultLang} />
            :
            null}>
          {output}
        </TitledPanel>
        :
        output
    );
  }
}

TypeDisplay.defaultProps = {
  higlightNotFound: false,
  wrap: true,
  offerInheritance: false
};

TypeDisplay.propTypes = {
  id: PropTypes.string.isRequired,
  offerInheritance: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  title: PropTypes.string,
  additionalEBP: PropTypes.array,
  wrap: PropTypes.bool,
  highlightNotFound: PropTypes.bool,
  isLoading: PropTypes.bool,
  classIRIs: PropTypes.array,
  source: PropTypes.string,
  textRef: PropTypes.string,
  defaultLang: PropTypes.string.isRequired,
  viewLang: PropTypes.string,
  graph: PropTypes.string.isRequired,
  projectIRI: PropTypes.string.isRequired,
  projects: PropTypes.array,
  ontologyProjectAssociation: PropTypes.array,
  property: PropTypes.string,
  onAddClass: PropTypes.func,
  values: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ])
};

export default TypeDisplay;